<template>
  <div class="index">
    <TV
      :articles="tvArticles"
      :meta="fetchResponse?.data.meta"
      class="index__tv"
    />
    <div class="index__content">
      <TwoColumn>
        <div class="index__main">
          <div
            v-if="!!fetchError"
            class="index__egror"
          >
            <p>Что-то пошло не так :(</p>
            <p v-if="fetchError">{{ fetchError }}</p>
          </div>

          <div
            v-else
            :class="classArticlesFeed"
            class="index__articles-feed"
          >
            <ArticlesFeedItem
              v-for="(item, index) in articlesFeed"
              :key="index"
              :articles-config="articlesConfig"
              :articles-feed="articlesFeed"
              :index="index"
              :item="item"
              class="index__articles-feed-item"
              @load-articles="loadNewArticles"
            />
            <ArticleCardSmallPlaceholder v-if="fetchStatus === 'pending'" />
          </div>

          <div
            v-if="isFull"
            class="index__pagination"
          >
            <BaseButton
              :href="`/page/${MAX_PAGES + 1}`"
              class="index__pagination-button"
              size="big"
              @click="sendAnalyticsLoadMore"
            >
              Показать ещё
            </BaseButton>
          </div>
        </div>
      </TwoColumn>
    </div>
  </div>
</template>

<script lang="ts">
  import BaseButton from '@devhacker/shared/components/base/BaseButton.vue';
  import { ARCHIVES_ARTICLES_COUNT_ON_PAGE, TV_COUNT_ARTICLES } from '~/constants';
  import { getArraySplitIntoChunks } from '~/utils/arrays';
  import type { TArticle } from '~/types/common';
  import TV from '~/components/TV.vue';
  import ArticleCardSmallPlaceholder from '~/components/ArticleCardSmallPlaceholder/ArticleCardSmallPlaceholder.vue';
  import ArticlesFeedItem from '~/components/ArticlesFeedItem.vue';
  import { useArchivesStore } from '~/store/archives';
  import { useSeoStore } from '~/store/seo';
  import TwoColumn from '~/components/templates/TwoColumn.vue';

  const MAX_PAGES = 4;

  export default defineNuxtComponent({
    name: 'IndexPage',

    components: {
      BaseButton,
      TV,
      ArticleCardSmallPlaceholder,
      ArticlesFeedItem,
      TwoColumn,
    },

    // для отправки аналитики
    // ArticleCardSmall -> CardFooter
    provide() {
      return {
        eventCategory: () => 'Главная',
        eventActionArticle: () => 'Списочные карточки',
        eventActionComments: () => 'Комментарии',
        eventActionCategory: () => 'Рубрика',

        eventElementListCard: () => 'Списочная карточка',
        eventName: () => 'Выбор материала_главная',
      };
    },

    async setup() {
      const { $api } = useNuxtApp();
      const archiveStore = useArchivesStore();
      const seoStore = useSeoStore();
      const route = useRoute();

      const page = ref(1);
      const tvArticles = ref([] as TArticle[]);
      const articles = ref([] as TArticle[]);
      const articlesFeed = computed(() => getArraySplitIntoChunks(articles.value, 5));

      // Refactor feed item and remove this config
      const articlesConfig = computed(() => ({
        pack: {
          page: page.value,
          limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE,
          maxPages: MAX_PAGES,
        },
        chunk: {
          size: 5,
          analyticsSize: 15,
        },
      }));

      const {
        data: fetchResponse,
        status: fetchStatus,
        error: fetchError,
        execute: fetchMorePosts,
      } = await useAsyncData('home-posts', () =>
        $api.archives.homePosts({
          page: page.value,
          limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE + (page.value === 1 ? TV_COUNT_ARTICLES : 0),
          offset: page.value === 1 ? 0 : TV_COUNT_ARTICLES,
        }),
      );

      const loadNewArticles = () => {
        if (page.value >= MAX_PAGES || fetchStatus.value === 'pending') return;
        page.value++;
        fetchMorePosts();
      };

      watch(
        fetchResponse,
        (response) => {
          if (!response?.data) return;

          const { items, seo, meta } = response.data;
          const pagesRoute = route.fullPath.replace(route.hash, '');

          archiveStore.setArticles(items);
          archiveStore.setMeta(meta);
          seoStore.addSeoByPath(pagesRoute, seo);

          let feedArticles = items;

          if (page.value === 1) {
            tvArticles.value = items.slice(0, TV_COUNT_ARTICLES);
            feedArticles = items.slice(TV_COUNT_ARTICLES);
          }

          articles.value.push(...feedArticles);
        },
        { immediate: true },
      );

      if (fetchResponse.value?.data.seo) {
        const { title, description, canonical, og_image, schema, site_icon, robots } =
          fetchResponse.value.data.seo;
        const ldJsonContent = schema?.replace(/<script.*?>(.*)<\/script>/, '$1');

        useHead({
          title,

          meta: [
            { key: 'og:title', property: 'og:title', content: title },
            {
              key: 'og:image',
              property: 'og:image',
              content: og_image || site_icon,
            },
            {
              key: 'og:description',
              property: 'og:description',
              content: description,
            },
            { key: 'og:url', property: 'og:url', content: canonical },
            { key: 'description', name: 'description', content: description },
            {
              key: 'robots',
              name: 'robots',
              content: robots,
            },
            // twiiter
            { key: 'twitter:title', property: 'twitter:title', content: title },
            {
              key: 'twitter:description',
              property: 'twitter:description',
              content: description,
            },
            {
              key: 'twitter:card',
              property: 'twitter:card',
              content: 'summary_large_image',
            },
            { key: 'twitter:image', property: 'twitter:image', content: og_image },
            {
              key: 'yandex_recommendations_kill',
              property: 'yandex_recommendations_kill',
              content: '1',
            },
          ],

          script: [
            {
              type: 'application/ld+json',
              innerHTML: ldJsonContent,
            },
          ],

          link: [{ key: 'canonical', rel: 'canonical', href: canonical }],
        });
      }

      return {
        articlesConfig,
        tvArticles,
        articlesFeed,
        page,
        fetchStatus,
        fetchError,
        fetchResponse,
        loadNewArticles,
        MAX_PAGES,
      };
    },

    computed: {
      isFull(): boolean {
        return this.page >= MAX_PAGES;
      },

      classArticlesFeed() {
        const className = 'index__articles-feed';
        return {
          [`${className}--full`]: this.isFull,
        };
      },
    },

    methods: {
      // Функция для отправки аналитики клика показать еще
      sendAnalyticsLoadMore(): void {
        // const currentSlug = getSlugFromUrl(this.$route.fullPath);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .index {
    // * для брендирования, чтобы баннер не перекрывал контент
    z-index: 2;

    @include container;

    @include tablet {
      margin-top: $spacing-6;
      padding-bottom: $spacing-10;
    }

    @include big-tablet {
      padding-bottom: $spacing-12;
    }

    @include desktop {
      max-width: 1280px;
    }

    :deep() {
      .template--two-column {
        @include desktop {
          grid-column-gap: 0px;
          column-gap: 0px;
        }
      }
      .template__sidebar {
        display: none;
        @include desktop {
          display: block;
        }
      }
    }

    &__tv {
      @include tablet {
        margin-bottom: $spacing-5;
      }

      @include big-tablet {
        margin-bottom: $spacing-6;
      }
    }

    &__main {
      background: black;

      @include tablet {
        background: #ffffff;
      }
    }

    &__articles-feed-item {
      &:not(:last-child):not(.articles-feed-item--widgets-area-empty) {
        margin-bottom: $spacing-8;
        @include tablet {
          margin-bottom: $spacing-4;
        }
        @include big-tablet {
          margin-bottom: $spacing-6;
        }
      }
    }

    &__pagination {
      background: white;
      border-radius: 0 0 32px 32px;
      padding: $spacing-4;
      margin: -1px 2px 0;
      display: flex;

      @include tablet {
        padding: $spacing-4 $spacing-6 0;
        margin: 0;
      }
      @include big-tablet {
        padding: $spacing-4 $spacing-8 0;
      }
      @include desktop {
        padding: $spacing-4 $spacing-10 0;
        margin-right: 40px;
      }

      &-button {
        flex-grow: 1;
      }
    }
  }
</style>
